/**
 * Service để quản lý trạng thái checkout cells trên client
 * Lưu danh sách các ô đang được checkout bởi người khác
 */

export interface CheckoutCellInfo {
  cell: number; // cell index
  expAt: number; // expiration timestamp in milliseconds
}

class CheckoutStateService {
  private checkoutCells: Map<number, number> = new Map(); // Map<cellIndex, expAt>

  /**
   * Update checkout list from server
   */
  updateCheckoutList(cells: CheckoutCellInfo[]): void {
    // Clear và rebuild map
    this.checkoutCells.clear();
    
    const now = Date.now();
    cells.forEach((item) => {
      // Chỉ add items chưa expire
      if (item.expAt && item.expAt > now) {
        this.checkoutCells.set(item.cell, item.expAt);
      }
    });

    // Dispatch event để các component khác có thể listen
    window.dispatchEvent(new CustomEvent('checkout-list-updated', {
      detail: { checkoutCells: Array.from(this.checkoutCells.entries()) }
    }));
  }

  /**
   * Add cells to checkout list (when receive broadcast)
   */
  addCheckoutCells(cells: CheckoutCellInfo[]): void {
    const now = Date.now();
    let hasChanges = false;

    cells.forEach((item) => {
      if (item.expAt && item.expAt > now) {
        this.checkoutCells.set(item.cell, item.expAt);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      // Dispatch event
      window.dispatchEvent(new CustomEvent('checkout-list-updated', {
        detail: { checkoutCells: Array.from(this.checkoutCells.entries()) }
      }));
    }
  }

  /**
   * Remove expired items
   */
  removeExpiredItems(): void {
    const now = Date.now();
    let hasChanges = false;

    this.checkoutCells.forEach((expAt, cellIndex) => {
      if (expAt <= now) {
        this.checkoutCells.delete(cellIndex);
        hasChanges = true;
      }
    });

    if (hasChanges) {
      // Dispatch event
      window.dispatchEvent(new CustomEvent('checkout-list-updated', {
        detail: { checkoutCells: Array.from(this.checkoutCells.entries()) }
      }));
    }
  }

  /**
   * Check if a cell is currently being checked out
   */
  isCellCheckedOut(cellIndex: number): boolean {
    // Remove expired items first
    this.removeExpiredItems();

    const expAt = this.checkoutCells.get(cellIndex);
    if (!expAt) return false;

    // Check if still valid
    if (expAt <= Date.now()) {
      this.checkoutCells.delete(cellIndex);
      return false;
    }

    return true;
  }

  /**
   * Get all checked out cells
   */
  getCheckedOutCells(): number[] {
    this.removeExpiredItems();
    return Array.from(this.checkoutCells.keys());
  }

  /**
   * Get expiration time for a cell (if exists)
   */
  getCellExpAt(cellIndex: number): number | null {
    this.removeExpiredItems();
    const expAt = this.checkoutCells.get(cellIndex);
    if (expAt && expAt > Date.now()) {
      return expAt;
    }
    return null;
  }
}

export const checkoutStateService = new CheckoutStateService();
export default checkoutStateService;

